import { useMemo } from 'react';

import { ErrorOutline, InfoOutlined } from '@mui/icons-material';
import WarningIcon from '@mui/icons-material/Warning';
import { Box, Chip, Skeleton, Stack, Tooltip, Typography } from '@mui/material';

import { CampaignChannelLabels, CampaignChannels } from 'domains/campaigns/types';
import { greyChipStyles } from 'domains/core/components/Chip/Chip';
import { Segment } from 'domains/segments/types';
import { hasInvalidRelativeDates } from 'domains/segments/utils';
import useAudienceCount, { UseAudienceCountResponse } from 'hooks/queries/useAudienceCount';
import useFeatureSettings from 'hooks/queries/useFeatureSettings';
import { RecordType } from 'models/enums';
import { getNumberWithCommas } from 'utils';
import { removeTypeFromId } from 'utils/folders';

const chipStyles = { ...greyChipStyles, textTransform: 'capitalize' };

const CampaignChannelTooltips: { [key in CampaignChannels]?: string } = {
    [CampaignChannels.DIRECT_MAIL]: 'This count filters out contacts missing mailing addresses',
    [CampaignChannels.EMAIL]: 'This count filters out unsubscribed and invalid email addresses',
    [CampaignChannels.SMS]: 'This count filters out opted out and invalid phone numbers',
    [CampaignChannels.VOICE_CALL]: 'This count filters out opted out and invalid phone numbers',
};

type AudienceErrorProps = {
    message: string;
};

const AudienceError = ({ message }: AudienceErrorProps) => (
    <Stack direction="row" justifyContent="flex-start" data-testid="audience-error">
        <ErrorOutline sx={{ color: 'error.main', mr: 1 }} />
        <Typography variant="body2">{message}</Typography>
    </Stack>
);

type AudienceCountProps = {
    count: string;
    isCountMissing: boolean;
    isLoading: boolean;
    channel?: CampaignChannels;
};

const AudienceCount = ({ count, isCountMissing, isLoading, channel }: AudienceCountProps) => {
    const getDataTestAttribute = (channel?: CampaignChannels, suffix: string = 'segment-count'): string =>
        `${channel ? channel.toLowerCase() + '-' : ''}${suffix}`;

    const channelLabel = CampaignChannelLabels[channel] ? `${CampaignChannelLabels[channel]} ` : '';
    const tooltipTitle = CampaignChannelTooltips[channel] || '';
    const countAsNum = Number(count);
    const countLabelSuffix = countAsNum === 1 ? 'contact' : 'contacts';
    const countLabel = ` ${channelLabel}${countLabelSuffix}`;

    const errorLabel = channelLabel ? `${channelLabel}count not available` : 'Count not available';

    return (
        <Stack direction="row" justifyContent="flex-start">
            {isCountMissing && <WarningIcon sx={{ color: 'warning.main', mr: 1 }} />}
            {isCountMissing ? (
                <Typography variant="body2" width="100%" data-testid="audience-count-error">
                    {errorLabel}
                </Typography>
            ) : isLoading ? (
                <Typography variant="body2" width="100%">
                    <Skeleton data-testid="segment-count-skeleton" />
                </Typography>
            ) : (
                <Stack direction="row" alignItems="center" columnGap={0.5}>
                    <Typography variant="body2" width="100%">
                        <span data-test={getDataTestAttribute(channel)}>{getNumberWithCommas(countAsNum)}</span>
                        {countLabel}
                    </Typography>
                    {channel && (
                        <Tooltip
                            title={tooltipTitle}
                            sx={{ pointerEvents: 'auto' }}
                            // Setting pointerEvents: 'auto' allows the Tooltip to show on hover if it's used in the label.
                        >
                            <InfoOutlined sx={{ fontSize: 14, mt: 0.5 }} />
                        </Tooltip>
                    )}
                </Stack>
            )}
        </Stack>
    );
};

type Props = {
    segment: Segment;
};

const AudienceDetails = ({ segment }: Props) => {
    const { id: idWithType, segmentDefinition } = segment;
    const id = removeTypeFromId(idWithType);

    const { data: featureSettings, isSuccess: isFeatureSettingsSuccess } = useFeatureSettings();

    const isEmailEnabled = isFeatureSettingsSuccess && featureSettings?.email_enabled !== false;
    const isDirectMailEnabled = isFeatureSettingsSuccess && featureSettings?.directmail_enabled;
    const isSMSEnabled = isFeatureSettingsSuccess && featureSettings?.sms_enabled !== false;
    const isVoiceEnabled = isFeatureSettingsSuccess && featureSettings.voice_enabled;

    const { data, isError, isInitialLoading: isLoading, error } = useAudienceCount(id, undefined, {
        enabled: !hasInvalidRelativeDates(segment),
    });

    const countData = data as UseAudienceCountResponse;

    const recordTypeChips = useMemo(() => {
        const contactRecordType = segmentDefinition.contactRecordType;

        if (
            !contactRecordType ||
            contactRecordType.length === 0 ||
            contactRecordType.includes(RecordType.ALL_CONTACTS)
        ) {
            return <Chip label={RecordType.ALL_CONTACTS} sx={chipStyles} />;
        }

        return (
            <Stack direction="row" flexWrap="wrap" spacing={1}>
                {contactRecordType.map((recordType, idx) => (
                    <Chip key={recordType + idx} label={recordType} sx={chipStyles} />
                ))}
            </Stack>
        );
    }, [segmentDefinition.contactRecordType]);

    const showApiError = !isLoading && isError;

    const getIsCountMissing = (count: string) => !isLoading && !count;

    return (
        <>
            <Typography>Audience Details</Typography>
            {showApiError ? (
                <AudienceError message={error?.message} />
            ) : (
                <>
                    <AudienceCount
                        count={countData?.all}
                        isCountMissing={getIsCountMissing(countData?.all)}
                        isLoading={isLoading}
                    />
                    {isEmailEnabled && (
                        <AudienceCount
                            channel={CampaignChannels.EMAIL}
                            count={countData?.email}
                            isCountMissing={getIsCountMissing(countData?.email)}
                            isLoading={isLoading}
                        />
                    )}
                    {isSMSEnabled && (
                        <AudienceCount
                            channel={CampaignChannels.SMS}
                            count={countData?.sms}
                            isCountMissing={getIsCountMissing(countData?.sms)}
                            isLoading={isLoading}
                        />
                    )}
                    {isDirectMailEnabled && (
                        <AudienceCount
                            channel={CampaignChannels.DIRECT_MAIL}
                            count={countData?.directmail}
                            isCountMissing={getIsCountMissing(countData?.directmail)}
                            isLoading={isLoading}
                        />
                    )}
                    {isVoiceEnabled && (
                        <AudienceCount
                            channel={CampaignChannels.VOICE_CALL}
                            count={countData?.voice}
                            isCountMissing={getIsCountMissing(countData?.voice)}
                            isLoading={isLoading}
                        />
                    )}
                    <Box mt={1}>{recordTypeChips}</Box>
                </>
            )}
        </>
    );
};

export default AudienceDetails;
